import { Fragment, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import PropTypes from "prop-types";
import TransformEditor from "./TransformEditor.jsx";

function emptyRule() {
  return {
    source_column: "",
    target_field: "",
    required: false,
    transform: null,
  };
}

function RelationEditor({ relation, on_change }) {
  // Editing model is an index-keyed array of [parent_column, row_field] pairs,
  // NOT the pull object — two in-progress rows can transiently share a key
  // (e.g. both empty) without collapsing. Serialized to an object (dropping
  // empty keys) only when propagating up.
  const [entries, setEntries] = useState(() => Object.entries(relation.pull ?? {}));

  const set_pull = (new_entries) => {
    setEntries(new_entries);
    on_change({
      ...relation,
      pull: Object.fromEntries(new_entries.filter(([k]) => k !== "")),
    });
  };

  const add_entry = () => set_pull([...entries, ["", ""]]);
  const remove_entry = (idx) => set_pull(entries.filter((_, i) => i !== idx));
  const update_entry = (idx, col, val) =>
    set_pull(entries.map(([k, v], i) => i === idx ? (col === 0 ? [val, v] : [k, val]) : [k, v]));

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover", borderRadius: 1, my: 0.5 }}>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Relation: Der Wert dieser Spalte ist eine externe Referenz auf{" "}
        <strong>{relation.entity}</strong> und wird beim Import zur internen ID aufgelöst.
        Pull-Felder übernehmen zusätzlich Werte aus dem aufgelösten Datensatz in diese Zeile
        (vorhandene Zeilenwerte werden nicht überschrieben).
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={entries.length ? 1 : 0}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">Entität:</Typography>
          <Chip label={relation.entity} size="small" variant="outlined" />
        </Box>
        <Box display="flex" alignItems="center" gap={1} ml="auto">
          <Typography variant="caption" color="text.secondary">
            Pull-Felder (Elternfeld → Zeilen-Spalte):
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={add_entry}>
            Hinzufügen
          </Button>
        </Box>
      </Box>
      {entries.map(([k, v], idx) => (
        <Box key={idx} display="flex" gap={1} alignItems="center" mb={0.5}>
          <TextField
            size="small"
            label="Elternfeld"
            value={k}
            onChange={(e) => update_entry(idx, 0, e.target.value)}
            sx={{ flex: 1 }}
            placeholder="z.B. vertrag_id_ov"
          />
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>→</Typography>
          <TextField
            size="small"
            label="Zeilen-Spalte"
            value={v}
            onChange={(e) => update_entry(idx, 1, e.target.value)}
            sx={{ flex: 1 }}
            placeholder="z.B. vertrag_id_ov"
          />
          <IconButton size="small" onClick={() => remove_entry(idx)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

RelationEditor.propTypes = {
  relation: PropTypes.shape({
    entity: PropTypes.string.isRequired,
    pull: PropTypes.object,
  }).isRequired,
  on_change: PropTypes.func.isRequired,
};

export default function MappingRuleEditor({
  mappings,
  entity_fields,
  onChange,
}) {
  const field_entries = Object.entries(entity_fields ?? {});

  const addRule = () => onChange([...mappings, emptyRule()]);

  const removeRule = (index) =>
    onChange(mappings.filter((_, i) => i !== index));

  const updateRule = (index, key, value) => {
    const next = mappings.map((r, i) => {
      if (i !== index) return r;
      const updated = { ...r, [key]: value };
      if (key === "transform" && value?.type === "concat") {
        updated.source_column = "";
      }
      if (key === "target_field") {
        const field_meta = entity_fields[value];
        if (field_meta?.entity) {
          // entering a relation field: initialize relation, preserve existing pull if entity matches
          const keep_pull = r.relation?.entity === field_meta.entity ? r.relation.pull : {};
          updated.relation = { entity: field_meta.entity, pull: keep_pull };
        } else {
          delete updated.relation;
        }
      }
      return updated;
    });
    onChange(next);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="subtitle2">Mapping-Regeln</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
          Regel hinzufügen
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Quellspalte</TableCell>
            <TableCell>Zielfeld</TableCell>
            <TableCell align="center">Pflichtfeld</TableCell>
            <TableCell>Transform</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {mappings.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={5}
                align="center"
                sx={{ color: "text.secondary" }}
              >
                Noch keine Regeln definiert.
              </TableCell>
            </TableRow>
          )}
          {mappings.map((rule, i) => {
            const is_concat = rule.transform?.type === "concat";
            const catalog_entity = entity_fields[rule.target_field]?.entity;
            const show_relation = !!(rule.relation || catalog_entity);
            const relation_value = rule.relation ?? (catalog_entity ? { entity: catalog_entity, pull: {} } : null);
            return (
              <Fragment key={i}>
                <TableRow>
                  <TableCell sx={{ minWidth: 140 }}>
                    {is_concat ? (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                        (mehrere Spalten)
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        value={rule.source_column}
                        onChange={(e) =>
                          updateRule(i, "source_column", e.target.value)
                        }
                        placeholder="z.B. Nr"
                      />
                    )}
                  </TableCell>

                  <TableCell sx={{ minWidth: 180 }}>
                    {field_entries.length > 0 ? (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Zielfeld</InputLabel>
                        <Select
                          value={rule.target_field}
                          label="Zielfeld"
                          onChange={(e) =>
                            updateRule(i, "target_field", e.target.value)
                          }
                        >
                          {field_entries.map(([key, meta]) => (
                            <MenuItem key={key} value={key}>
                              {meta.label ?? key}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        value={rule.target_field}
                        onChange={(e) =>
                          updateRule(i, "target_field", e.target.value)
                        }
                        placeholder="Feldname"
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Checkbox
                      checked={!!rule.required}
                      onChange={(e) => updateRule(i, "required", e.target.checked)}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <TransformEditor
                      transform={rule.transform}
                      onChange={(t) => updateRule(i, "transform", t)}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeRule(i)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {show_relation && relation_value && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, pb: 1, borderBottom: "none" }}>
                      <RelationEditor
                        key={`relation-${i}-${relation_value.entity}`}
                        relation={relation_value}
                        on_change={(rel) => updateRule(i, "relation", rel)}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

MappingRuleEditor.propTypes = {
  mappings: PropTypes.array.isRequired,
  entity_fields: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};
